import { Chip, CircularProgress } from "@mui/material"
import { FC } from "react"
import type {
  MonthlyExtractRunDto,
  MonthlyExtractWindow,
} from "src/features/things/monthlyExtractApi"

/** How often a page re-reads while a run is pending or running (DoD3). */
export const RUN_POLL_MS = 3_000

export const isActive = (status: MonthlyExtractRunDto["status"] | "queued") =>
  status === "pending" || status === "running" || status === "queued"

const STATUS_COLOR = {
  pending: "default",
  running: "info",
  done: "success",
  failed: "error",
  queued: "default",
} as const

export const MonthlyRunStatusChip: FC<{ status: MonthlyExtractRunDto["status"] | "queued" }> = ({
  status,
}) => (
  <Chip
    size="small"
    label={status}
    color={STATUS_COLOR[status]}
    variant={status === "done" || status === "failed" ? "filled" : "outlined"}
    icon={isActive(status) ? <CircularProgress size={12} sx={{ ml: 0.75 }} /> : undefined}
    data-testid={`run-status-${status}`}
  />
)

/** LOCKER-496 — what each window extracts, as the dialog and the run pages say it. */
export const WINDOW_LABEL: Record<MonthlyExtractWindow, string> = {
  utc: "UTC month (as published)",
  toronto: "Toronto month",
}

/** A run recorded before LOCKER-496 carries no window: it was a `utc` run. */
export const runWindow = (run: Pick<MonthlyExtractRunDto, "window">): MonthlyExtractWindow =>
  run.window ?? "utc"

export const MonthlyRunWindowChip: FC<{ window: MonthlyExtractWindow }> = ({ window }) => (
  <Chip
    size="small"
    variant="outlined"
    label={window}
    title={WINDOW_LABEL[window]}
    data-testid={`run-window-${window}`}
  />
)

export const fmtDuration = (ms: number | undefined): string => {
  if (ms === undefined) return "—"
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} s`
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`
}

/** "2026-08" → "August 2026", in the viewer's locale. */
export const fmtPeriod = (period: string): string => {
  const [y, m] = period.split("-").map(Number)
  if (!y || !m) return period
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  })
}

export const fmtBytes = (b: number): string =>
  b < 1024 ? `${b} B` : b < 1024 ** 2 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 ** 2).toFixed(1)} MB`

/** Expiry text, and whether it has passed — the reason a share can be replaced (LOCKER-500 N6). */
export const shareExpiry = (iso: string | undefined, now = Date.now()) => {
  if (!iso) return { label: "—", expired: false }
  const t = new Date(iso).getTime()
  const days = Math.ceil((t - now) / 86_400_000)
  const date = new Date(iso).toLocaleDateString()
  return t <= now
    ? { label: `expired ${date}`, expired: true }
    : { label: `${date} (${days} d)`, expired: false }
}
